import { ABB_PLAY, ABB_STOP, ABB_REFRESH, ABB_WAITING } from '../../utilities/IconConstants';
import { Status } from "../sample/Status";
import { IDirectionCount } from '../measurement/DirectionCount';
import { IPositionCount } from '../measurement/PositionCount';
import { ITestplanData } from '../sample/SampleViewData';

export interface IStatus {
    id: string;
    name: string;
}

// limits 
export interface IValueRange {
    id: string;
    name: string;
}

// new
export interface ICount {
    completedCounts: number;
    totalCounts: number;
}
export interface IMeasurementProperties {
    id: string;
    name: string;
    unit: string;
    directionId: string;
    avg?: string;
    decimals?: number;
}
interface IDirectionMean {
    directionId: string,
    mean: number,
    propertyId: string
}
export interface IReadings {
    count: number;
    directionId: string;
    position: number;
    propertyId: string;
    value?: any;
    valueRange: IValueRange; 
    positionName?: string;
    directionMeans?: IDirectionMean[];
    decimals?: number;
}

// new
export interface IIconStatus {
    name: string,
    color: string
}

// direction data
export interface ICountData {
    position: number;
    positionName: string;
    directions: IDirectionCountChildData[];
    selected: boolean;
}

export interface IDirectionCountChildData {
    id: string;
    name: string;
    completedCounts: number;
    totalCounts: number;
    selected: boolean;
    maxCount: number;
}


// statistics
export interface IStatistics {
    covariance: number;
    directionId: string;
    mean: number;
    median: number;
    position: number;
    propertyId: string;
    standardDeviation: number
}

export interface IPropertyMean {
    propertyId: string;
    mean: number
}

export interface IPositionMeans {
    propertyId: string;
    name?: number;
    position: number;
    mean: number
}

export interface ISampleDetails {
    id: string;
    name: string;
    count: ICount;
    properties: IMeasurementProperties[];
    readings: IReadings[];
    countData: ICountData[];
    completionPercentage: string;
    iconStatus: IIconStatus;
    instrumentStatus: IStatus;
    instrumentSampleStatus?: IStatus;
    status: { name: string, color: string };
    positionCount: IPositionCount[];
    statistics: IStatistics[];
    directionCounts: IDirectionCount[];
    propertyMeans: IPropertyMean[];
    positionMeans: IPositionMeans[];
    setPositionVal?: number;
    isAutoLine: boolean;
    allDirections: any[];
    testPlan: ITestplanData;
    measurementId: string;
    setSelectedDirection(position: number, id: string): void;
    updateDirectionCount(position: number, directionId: string, completedCounts: number): void;
    setCount(count: ICount): void;
    setAverage(propertyId: string, directionId: string, position: number, statistic: any): void;
    setPosition(index: number): void;
    setReadings(count: any, directionId: string, position: number, propertyId: string, value: any, valueRange: IValueRange, positionName?: string, decimals?:number): void;
    setStatus(instrumentStatus: IStatus): void;
    setMeasuredPosition(position: number): void;
    setCurrentMeasuringProperty(propertyId: string, position: number): void;
    setPositionCount(positionCount: IPositionCount[]): void;
    setDirectionCount(positionId: any, directionId: any, compCount: number): void;
}

interface Dictionary<T> {
    [Key: string]: T;
}


export class SampleDetails implements ISampleDetails {
    id: string;
    name: string;
    count: ICount;
    directionCounts: IDirectionCount[];
    positionCount: IPositionCount[];
    properties: IMeasurementProperties[];
    readings: IReadings[];
    countData: ICountData[];
    completionPercentage: string;
    iconStatus: IIconStatus;
    instrumentStatus: IStatus;
    instrumentSampleStatus?: IStatus;
    status: { name: string, color: string };
    statistics: IStatistics[];
    propertyMeans: IPropertyMean[];
    positionMeans: IPositionMeans[];
    setPositionVal: number;
    currentMeasuringProperty?: string;
    currentMeasuringPosition?: number;
    private readonly oldPositionCount: IPositionCount[];
    allDirections: any[];
    isAutoLine: boolean;
    testPlan: ITestplanData;
    measurementId: string;

    constructor(id: string,
        name: string,
        count: ICount,
        directionCounts: IDirectionCount[],
        properties: IMeasurementProperties[],
        readings: IReadings[],
        instrumentStatus: IStatus,
        positionCount: IPositionCount[],
        statistics: IStatistics[],
        propertyMeans: IPropertyMean[],
        positionMeans: IPositionMeans[],
        isAutoLine: boolean,
        testPlan: ITestplanData,
        measurementId: string,
        instrumentSampleStatus?: IStatus,
    ) {
        this.id = id;
        this.name = name;
        this.directionCounts = directionCounts ? directionCounts : [];
        this.properties = properties;
        this.isAutoLine = isAutoLine;
        this.testPlan = testPlan;
        this.measurementId = measurementId;
        this.instrumentSampleStatus = instrumentSampleStatus;
        if (readings === undefined || readings === null) {
            this.readings = [];
        } else {
            this.readings = readings;
        }

        if (statistics === undefined || statistics === null) {
            this.statistics = [];
        } else {
            this.statistics = statistics;
        }

        this.countData = [{
            position: -1,
            positionName: "",
            directions: [
                {
                    id: "All",
                    name: "All",
                    completedCounts: 0,
                    totalCounts: 0,
                    selected: true,
                    maxCount: 0
                }
            ],
            selected: true
        }];
        this.iconStatus = { name: ABB_PLAY, color: 'blue' };
        if (positionCount === null || positionCount === undefined) {
            this.positionCount = [];
        } else {
            this.positionCount = positionCount;
            this.oldPositionCount = positionCount;
            if (!this.isAutoLine) {
                if (this.positionCount.length > 0) {
                    this.positionCount[0].selected = true
                }
            }
        }

        this.propertyMeans = propertyMeans;
        this.positionMeans = positionMeans;
        this.setPositionVal = 0;


        this.setCount(count);
        this.setAllDirections();
        this.setStatus(instrumentStatus ? instrumentStatus : instrumentSampleStatus);
        if (this.isAutoLine) {
            this.setPosData();
        }

        this.allDirections = [];
        this.setDirections();
    }
    private setPosData() {
        if (this.positionCount.length > 0) {
            this.positionCount = [];
            const readingData: any[] = [];

            const unique = Array.from(new Set(this.readings));
            const uniqueValues = unique.filter((element, index) =>
                unique.findIndex(obj => obj.propertyId === element.propertyId && obj.position === element.position
                ) === index)
            uniqueValues.forEach((data, index) => {
                const posCount = this.oldPositionCount.find(x => x.position === data.position);
                let selected = false;
                if (posCount.index !== undefined) {
                    selected = posCount.index === 0 ? true : false;
                } else {
                    selected = index === 0 ? true : false;
                }
                const positionCount = {
                    position: data.position,
                    totalCounts: posCount.totalCounts,
                    completedCounts: posCount.completedCounts,
                    selected,
                    index
                }
                this.positionCount.push(positionCount);
            });
        }
    }


    // Set Position count from Measurement Hub
    public setPositionCount(positionCount: IPositionCount[]): void {
        this.positionCount = positionCount;
    }

    // Set direction count from Measurement Hub
    public setDirectionCount(positionId: any, directionId: any, compCount: number): void {
        let countData = this.countData?.find(x => x.position === positionId);
        let direction = countData?.directions?.find(x => x.id === directionId);
        if (direction) {
            direction.completedCounts = compCount;
        }
        let completedCountsForPosition = 0;
    countData?.directions.forEach((x:any)=>{
      if(x.name !== "All"){
        completedCountsForPosition+=x.completedCounts
      }
    })
    let allDirection = countData?.directions?.find((x) => x.name === "All");
    if (allDirection) {
      allDirection.completedCounts=completedCountsForPosition;
    }
    }
    
    public setCurrentMeasuringProperty(propertyId: string, position: number) {
        this.currentMeasuringProperty = propertyId;
        this.currentMeasuringPosition = position;
    }
    private setDirections = () => {
        this.directionCounts.map((data, index) => {
            const findIndx = this.allDirections.find(x => x.id === data.direction.id);
            if (findIndx === undefined) {
                this.allDirections.push({ id: data.direction.id, name: data.direction.name, order : data.direction.order })
            }
        })
    }
    private setAllDirections() {
        if (this.directionCounts?.length > 0) {
            this.countData = [];
        }
        const maxCountMapping: Dictionary<number> = {};

        this.directionCounts?.forEach((entity) => {
            if (this.countData.some(x => x.position === entity.position)) {
                const count = this.countData.find(x => x.position === entity.position);

                if (maxCountMapping[entity.direction.id] === undefined) {
                    maxCountMapping[entity.direction.id] = entity.totalCounts
                } else {
                    maxCountMapping[entity.direction.id] = Math.max(maxCountMapping[entity.direction.id], entity.totalCounts);
                }

                count.directions.push({
                    id: entity.direction.id,
                    name: entity.direction.name,
                    completedCounts: entity.completedCounts,
                    totalCounts: entity.totalCounts,
                    selected: false,
                    maxCount: maxCountMapping[entity.direction.id]
                });

                const allDirection = count.directions.find(x => x.id === "All_" + entity.position + "_" + this.id);
                allDirection.completedCounts += entity.completedCounts;
                allDirection.totalCounts += entity.totalCounts;
            } else {

                if (maxCountMapping[entity.direction.id] === undefined) {
                    maxCountMapping[entity.direction.id] = entity.totalCounts
                } else {
                    maxCountMapping[entity.direction.id] = Math.max(maxCountMapping[entity.direction.id], entity.totalCounts);
                }

                const direction = {
                    id: entity.direction.id,
                    name: entity.direction.name,
                    completedCounts: entity.completedCounts,
                    totalCounts: entity.totalCounts,
                    selected: false,
                    maxCount: maxCountMapping[entity.direction.id]
                }

                const allId = "All_" + entity.position + "_" + this.id;
                if (maxCountMapping[allId] === undefined) {
                    maxCountMapping[allId] = entity.totalCounts;
                } else {
                    maxCountMapping[allId] = Math.max(maxCountMapping[allId], entity.totalCounts);
                }


                const allDirection = {
                    id: allId,
                    name: "All",
                    completedCounts: entity.completedCounts,
                    totalCounts: entity.totalCounts,
                    selected: true,
                    maxCount: maxCountMapping[allId]
                }

                const count = {
                    position: entity.position,
                    positionName: entity.name,
                    directions: [
                        allDirection,
                        direction
                    ],
                    selected: false
                }

                this.countData.push(count);
            }
        });
        this.countData[0].selected = true;
    }

    public setCount(count: ICount) {
        this.count = count;
        this.completionPercentage = ((count.completedCounts / count.totalCounts) * 100) + "%";
    }
    public setSelectedDirection(position: number, id: string) {
        const countData = this.countData.find(x => x.position === position);
        const prevSelectedDirection = countData.directions.find(y => y.selected);
        if (prevSelectedDirection !== undefined) {
            prevSelectedDirection.selected = false;
        }
        const toBeSelectedDirection = countData.directions.find(x => x.id === id);
        if (toBeSelectedDirection !== undefined) {
            toBeSelectedDirection.selected = true;
        }
    }
    public updateDirectionCount(position: number, directionId: string, completedCounts: number) {
        const directionCount = this.directionCounts.find(x => x.direction.id === directionId && x.position === position);
        const directionCountsIndex = this.directionCounts.findIndex(x => x.direction.id === directionId && x.position === position);

        directionCount.completedCounts = completedCounts;
        this.directionCounts[directionCountsIndex] = directionCount;
        this.setAllDirections();
    }

    public setStatus(instrumentStatus: IStatus) {
        if (instrumentStatus) {
            // NotMeasure = ABB_PLAY
            // measuring = stop
            // pending = play
            if (instrumentStatus.name === Status.NOTMEASURED) {
                this.status = { name: "Not Measured", color: "black" };
                this.iconStatus = { name: ABB_PLAY, color: "blue" }
            } else if (instrumentStatus.name === Status.MEASURING) {
                this.status = { name: "Measuring", color: "blue" };
                this.iconStatus = { name: ABB_STOP, color: "red" }
            } else if (instrumentStatus.name === Status.STOPPED) {
                this.status = { name: "Stopped", color: "blue" };
                this.iconStatus = { name: ABB_REFRESH, color: "blue" }
            } else if (instrumentStatus.name === Status.INUSE) {
                this.status = { name: "Waiting", color: "black" };
                this.iconStatus = { name: ABB_WAITING, color: "blue" }
            }
            else if (instrumentStatus.name === Status.PENDING) {
                this.status = { name: "Not Measured", color: "black" };
                this.iconStatus = { name: ABB_PLAY, color: "blue" }
            }
            else {
                this.status = { name: "Completed", color: "blue" };
                this.iconStatus = { name: ABB_REFRESH, color: "blue" }
            }
        }
        else {
            this.status = { name: "Not Measured", color: "black" };
            this.iconStatus = { name: ABB_PLAY, color: "blue" }
        }
    }

    public setAverage(propertyId: string, directionId: string, position: number, statistic: any) {

        this.statistics.push({
            covariance: statistic.covariance || 0,
            directionId,
            mean: statistic.mean,
            median: statistic.median,
            position,
            propertyId,
            standardDeviation: statistic.standardDeviation
        });
    }

    public setPosition(index: number) {

        const prevPositionCount = this.positionCount.find(x => x.selected === true);
        if (prevPositionCount !== undefined) {
            prevPositionCount.selected = false;
        }
        const toBeSelectedPosition = this.positionCount.find(x => x.position === index);
        if (toBeSelectedPosition !== undefined) {
            toBeSelectedPosition.selected = true;
        }

        const prevCountData = this.countData.find(x => x.selected === true);
        if (prevCountData !== undefined) {
            prevCountData.selected = false
        }
        const toBeSelectedDirection = this.countData.find(x => x.position === index);
        if (toBeSelectedDirection !== undefined) {
            toBeSelectedDirection.selected = true;
        }

    }

    public setReadings(count: any, directionId: string, position: number, propertyId: string, value: any, valueRange: IValueRange, positionName?: string, decimals?: number) {
        this.readings.push({
            count, directionId, position, propertyId, value, valueRange, positionName, decimals
        });
    }

    public setMeasuredPosition(position: number) {
        this.setPositionVal = position;
        const prevPositionCount = this.positionCount.find(x => x.selected === true);
        if (prevPositionCount !== undefined) {
            prevPositionCount.selected = false;
        }
        const toBeSelectedPosition = this.positionCount.find(x => x.position === position);
        if (toBeSelectedPosition !== undefined) {
            toBeSelectedPosition.selected = true;
        }

        const prevCountData = this.countData.find(x => x.selected === true);
        if (prevCountData !== undefined) {
            prevCountData.selected = false
        }
        const toBeSelectedDirection = this.countData.find(x => x.position === position);
        if (toBeSelectedDirection !== undefined) {
            toBeSelectedDirection.selected = true;
        }
    }
}

const defaultSampleDetails = new SampleDetails("", "", { completedCounts: 0, totalCounts: 0 }, [], [], [],
    { id: "", name: "" }, [], [], [], [], false, null, "", null);
export { defaultSampleDetails };